import { Languages } from './../../../../../../libs/shared/src/constants/languages';
import { StorageService } from '@anihis/shared/src/services/storage.service';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, Subject } from 'rxjs';
import { DateTime } from 'luxon';
import { StorageKeyConstants } from '../constants/storage-key-constants';
import { getParsedJwt } from '../functions/parse-jwt';

interface CurrentUser {
  [key: string]: string | undefined; // Allow additional string keys
}

@Injectable({
  providedIn: 'root',
})
export class ApplicationStateService {
  private SecureStorageService = inject(StorageService);

  private _languageChanged = new BehaviorSubject<string>('en-GB');
  languageChanged$ = this._languageChanged
    .asObservable()
    .pipe(distinctUntilChanged());

  // Synchronous getter for current language value (useful for components
  // that prefer reading from a store-like property instead of subscribing
  // to the observable). Returns the current language id (e.g. 'en-GB').
  get language(): string {
    return this._languageChanged.getValue();
  }

  languageChanged(language: string): void {
    this._languageChanged.next(language);
  }

  private _timeZone!: string;
  private _date = new BehaviorSubject(
    DateTime.local({ zone: DateTime.local().zoneName as string })
      .startOf('day')
      .toISO() as string,
  );
  date$ = this._date
    .asObservable()
    .pipe(distinctUntilChanged((prev, curr) => prev === curr));

  constructor() {
    this._timeZone = DateTime.local().zoneName as string;
  }

  get timeZone() {
    return this._timeZone;
  }

  set date(date: string) {
    this._date.next(date);
  }

  getDate() {
    return this._date.getValue();
  }

  async getUserRole(): Promise<string | null> {
    const token = localStorage.getItem('id_token');

    if (!token) {
      console.warn('No id_token found in storage.');
      return null;
    }

    // Parse the id_token (plain JWT) and extract the role
    const parsedJwt = getParsedJwt(token) as CurrentUser | undefined;

    // active_role is always present in id_token (profile scope)
    const activeRole = (parsedJwt as any)?.['active_role'];
    if (activeRole && typeof activeRole === 'string') return activeRole;

    const roleKey =
      'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
    const role = parsedJwt
      ? ((parsedJwt as any)['role'] as string | undefined) ??
        (parsedJwt[roleKey] as string | undefined)
      : undefined;

    if (!role) {
      console.warn('Role not found in token.');
      return null;
    }

    return role;
  }

  async getUserUid(): Promise<string | null> {
    const token = localStorage.getItem('id_token');

    if (!token) return null;

    const parsedJwt = getParsedJwt(token) as CurrentUser | undefined;
    if (!parsedJwt) return null;

    return (
      (parsedJwt as any)?.sub ??
      parsedJwt[
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
      ] ??
      null
    );
  }
}
